'use client';

import DynamicMarquee from '@/components/DynamicMarquee';
import { Content, isFilled } from '@prismicio/client';
import type { LinkField } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';

/**
 * Props for `HeadlineLogosRow`.
 */
export type HeadlineLogosRowProps =
  SliceComponentProps<Content.HeadlineLogosRowSlice>;

type LogoItem = Content.HeadlineLogosRowSlice['primary']['logos'][number];

/**
 * Renders a single client logo, linking it to its case study when set (M-10).
 * `mobile` uses a fixed, larger height for the marquee; `desktop` keeps the
 * original 151px wrapped-row sizing.
 */
const renderLogo = (
  item: LogoItem,
  index: number,
  variant: 'mobile' | 'desktop',
) => {
  const logo = (
    <PrismicNextImage
      field={item.image}
      className={
        variant === 'mobile'
          ? 'object-contain h-24 w-auto'
          : 'object-contain h-16 w-auto'
      }
      width={151}
      height={151}
      sizes="151px"
      fallbackAlt=""
    />
  );
  // M-10: link the client logo to its case study when set.
  const logoLink = (item as unknown as { logo_link?: LinkField }).logo_link;
  const linkedLogo = isFilled.link(logoLink) ? (
    <PrismicNextLink
      field={logoLink}
      aria-label="View client case study"
      className="contents"
    >
      {logo}
    </PrismicNextLink>
  ) : (
    logo
  );

  if (variant === 'mobile') {
    return (
      <div key={index} className="flex items-center h-24 mx-8">
        {linkedLogo}
      </div>
    );
  }

  return (
    <div key={index} className="flex-shrink-0">
      <div className="h-20 flex items-center">{linkedLogo}</div>
    </div>
  );
};

/**
 * Component for "HeadlineLogosRow" Slices.
 */
const HeadlineLogosRow = ({ slice }: HeadlineLogosRowProps): JSX.Element => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#0E1010] py-16 md:py-24"
    >
      <div className="container mx-auto px-4 md:px-6">
        {/* Header Section */}
        <div className="max-w-4xl mb-12 md:mb-16">
          {slice.primary.title && (
            <h2 className="text-white font-medium text-[42px] leading-[120%] mb-6">
              <PrismicRichText
                field={slice.primary.title}
                components={{
                  heading2: ({ children }) => <span>{children}</span>,
                  paragraph: ({ children }) => <span>{children}</span>,
                  strong: ({ children }) => <strong>{children}</strong>,
                  em: ({ children }) => <em>{children}</em>,
                }}
              />
            </h2>
          )}
          {slice.primary.description && (
            <div className="text-white font-normal text-lg leading-[150%]">
              <PrismicRichText field={slice.primary.description} />
            </div>
          )}
        </div>

        {/* Logos Grid */}
        {slice.primary.logos && slice.primary.logos.length > 0 && (
          <>
            {/* Mobile: auto-scrolling marquee with larger logos */}
            <div className="md:hidden -mx-4">
              <DynamicMarquee speed={40} autoFill direction="left">
                {slice.primary.logos.map((item, index) =>
                  renderLogo(item, index, 'mobile'),
                )}
              </DynamicMarquee>
            </div>

            {/* Desktop: static wrapped row */}
            <div className="hidden md:flex flex-wrap justify-center items-center gap-x-12 gap-y-8 lg:justify-between">
              {slice.primary.logos.map((item, index) =>
                renderLogo(item, index, 'desktop'),
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default HeadlineLogosRow;
